import { SEGMENT_CONFIG, STRIKE_SELECTION_TYPES } from 'v2/common/constants/index';
import { extractIndividualSchemaField } from 'v2/modules/withRunForm/utils/signal';
import { Operand } from 'v3/quantConfig/types/signal.type';
import {
  OperandPointer,
  IntentPointer,
  QuickAdjustmentIntentPointer,
  PipeSignalPointer,
} from 'v3/quantConfig/types/pointer';
import { quantConfigIterator } from '../Iterator/index';
import { Intent, QuickAdjustmentConfig } from 'v3/quantConfig/types/intent.type';
import { QuantConfig } from '../types/quantConfig.type';
import { BaseReference } from './types/reference.type';
import { Pipe } from '../types/pipe.type';
import _ from 'lodash';
import { QuickAdjustmentAction } from 'v3/quantConfig/types/intent.type';

const findIntentReference = (
  caseIdx: number,
  intentIdx: number,
  quantConfig: QuantConfig
): BaseReference[] => {
  const intentReferences: BaseReference[] = [];

  const findOperandReference = <T extends OperandPointer | IntentPointer>(operand: Operand, pointer: T) => {
    const { caseIndex, intentId, basketId } = extractIndividualSchemaField(operand);

    if (
      ((intentId && intentId.includes(intentIdx)) || (basketId && basketId.includes(intentIdx))) &&
      _.parseInt(caseIndex) == caseIdx
    ) {
      intentReferences.push({ operand, pointer });
    }

    if (new RegExp(`\\bbasket_${intentIdx}_(?=\\w|$)`).test(_.toString(operand))) {
      intentReferences.push({ operand, pointer });
    }
  };

  const handlers = {
    eachOperand: (operand: Operand, pointer: OperandPointer) => {
      findOperandReference<OperandPointer>(operand, pointer);
    },

    eachIntent: (intent: Intent, pointer: IntentPointer) => {
      const { instrument, target, identifier } = intent;

      if (
        instrument?.type === SEGMENT_CONFIG.option &&
        instrument?.strike?.type === STRIKE_SELECTION_TYPES.IndicatorValueBasedStrikeIndex.value
      ) {
        findOperandReference<IntentPointer>(instrument.strike?.valuePath ?? '', pointer);
      }

      if (
        (parseInt(target?.label) == intentIdx && identifier.caseIndex == caseIdx) ||
        (new RegExp(`\\b(${intentIdx})\\b`).test(target?.label) && (_.isNull(caseIdx) || caseIdx == -1))
      ) {
        intentReferences.push({ pointer });
      }
    },

    eachQuickAdjustment: (quickAdjustment: QuickAdjustmentConfig, pointer: QuickAdjustmentIntentPointer) => {
      if (
        _.map(quickAdjustment.targets, _.toInteger).includes(_.toInteger(intentIdx)) &&
        pointer.caseIndex == caseIdx &&
        _.includes(
          [
            QuickAdjustmentAction.CloseLeg,
            QuickAdjustmentAction.ExecuteLeg,
            QuickAdjustmentAction.MoveToCost,
          ],
          quickAdjustment.action
        )
      ) {
        intentReferences.push({ pointer });
      }
    },
  };

  quantConfigIterator(quantConfig, handlers);

  return intentReferences;
};

const findPipeReference = (pipeIdx: number, quantConfig: QuantConfig): BaseReference[] => {
  const { name: pipeName } = quantConfig.pipeConfigs[pipeIdx] ?? {};

  const pipeReferences: BaseReference[] = [];

  const findOperandReference = <T extends OperandPointer | IntentPointer>(operand: Operand, pointer: T) => {
    if (new RegExp(`\\b${pipeName}\\b`).test(_.toString(operand))) pipeReferences.push({ operand, pointer });
  };

  const handlers = {
    eachOperand: (operand: Operand, pointer: OperandPointer) => {
      findOperandReference<OperandPointer>(operand, pointer);
    },

    eachIntent: (intent: Intent, pointer: IntentPointer) => {
      const { instrument, name } = intent;
      findOperandReference<IntentPointer>(name ?? '', pointer);

      if (
        instrument?.type === SEGMENT_CONFIG.option &&
        instrument?.strike?.type === STRIKE_SELECTION_TYPES.IndicatorValueBasedStrikeIndex.value
      ) {
        findOperandReference<IntentPointer>(instrument.strike?.valuePath ?? '', pointer);
      }
    },

    eachPipe: (pipe: Pipe, pointer: PipeSignalPointer) => {
      const operand = _.toString(pipe.config.valuePaths);
      if (operand.includes(`.${pipeName}.`)) {
        pipeReferences.push({ operand, pointer: pointer as OperandPointer });
      }
    },
  };

  quantConfigIterator(quantConfig, handlers);

  return pipeReferences;
};

const findCaseReference = (caseIdx: number, quantConfig: QuantConfig): BaseReference[] => {
  const filteredQuantConfig = {
    ...quantConfig,
    cases: quantConfig.cases.filter((_kase, index) => index !== caseIdx),
  };

  const caseReferences: BaseReference[] = [];

  const findOperandReference = (operand: Operand, pointer: OperandPointer | IntentPointer) => {
    const { caseIndex } = extractIndividualSchemaField(operand);

    if (_.parseInt(caseIndex) == caseIdx) caseReferences.push({ operand, pointer });
  };

  const handlers = {
    eachOperand: (operand: Operand, pointer: OperandPointer) => {
      findOperandReference(operand, pointer);
    },

    eachIntent: (intent: Intent, pointer: IntentPointer) => {
      const { instrument, target } = intent;

      if (
        instrument?.type === SEGMENT_CONFIG.option &&
        instrument?.strike?.type === STRIKE_SELECTION_TYPES.IndicatorValueBasedStrikeIndex.value
      ) {
        findOperandReference(instrument?.strike?.valuePath ?? '', pointer);
      }

      const targetCaseMatch = !_.isEmpty(intent?.target?.label) && parseInt(target?.caseIndex) === caseIdx;
      const intentCaseMatch = !_.isEmpty(intent?.caseIndex) && parseInt(intent?.caseIndex) === caseIdx;

      if (targetCaseMatch || intentCaseMatch) {
        caseReferences.push({ pointer });
      }
    },

    eachPipe: (pipe: Pipe, pointer: PipeSignalPointer) => {
      const caseIndex = _.parseInt(pipe.config.caseIndex ?? '');
      if (caseIndex === caseIdx) {
        caseReferences.push({
          pointer: {
            ...pointer,
            conditionIndex: caseIndex,
          },
        });
      }
    },

    eachQuickAdjustment: (quickAdjustment: QuickAdjustmentConfig, pointer: QuickAdjustmentIntentPointer) => {
      if (
        _.map(quickAdjustment.targets, _.toInteger).includes(_.toInteger(caseIdx)) &&
        _.includes([QuickAdjustmentAction.OpenCase, QuickAdjustmentAction.CloseCase], quickAdjustment.action)
      ) {
        caseReferences.push({ pointer });
      }
    },
  };

  quantConfigIterator(filteredQuantConfig, handlers);

  return caseReferences;
};

export { findIntentReference, findPipeReference, findCaseReference };
